import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { Calendar as CalIcon, CheckCircle2, IndianRupee, Info, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { DIAMOND_AMC, PACKAGES, LUXURY_PACKAGE } from '../mock';
import fbTrack from '../lib/fbpixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_PACKAGES = [DIAMOND_AMC, ...PACKAGES, LUXURY_PACKAGE];

export default function BookingModal({ open, onOpenChange, initialPackage }) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [carModel, setCarModel] = useState('');
  const [pkgId, setPkgId] = useState(initialPackage || 'diamond');
  const [date, setDate] = useState(null);
  const [payment, setPayment] = useState('token');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (initialPackage) setPkgId(initialPackage); }, [initialPackage]);

  const pkg = useMemo(() => ALL_PACKAGES.find(p => p.id === pkgId) || DIAMOND_AMC, [pkgId]);
  const isAMC = pkgId === 'diamond';
  const GST_RATE = 18;
  const fullWithGst = Math.round(pkg.offerPrice * (1 + GST_RATE / 100));

  const paymentOptions = isAMC
    ? [
        { id: 'token', label: 'બુકિંગ ટોકન', amount: 100, hint: 'ફક્ત ₹100 ભરીને સ્લોટ બુક કરો' },
        { id: 'partial', label: '25% ભરો', amount: Math.round(pkg.offerPrice * 0.25), hint: 'બાકી સર્વિસ વખતે' },
        { id: 'full', label: 'ફુલ પેમેન્ટ', amount: fullWithGst, hint: `Incl. ${GST_RATE}% GST` },
      ]
    : [
        { id: 'token', label: 'બુકિંગ ટોકન', amount: 100, hint: 'ફક્ત ₹100 ભરીને સ્લોટ બુક કરો' },
        { id: 'full', label: 'ફુલ પેમેન્ટ', amount: fullWithGst, hint: `Incl. ${GST_RATE}% GST` },
      ];

  const selected = paymentOptions.find(p => p.id === payment) || paymentOptions[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !mobile || !carModel || !date) {
      toast({ title: 'માહિતી અધૂરી', description: 'કૃપા કરીને બધો ફિલ્ડ ભરો' });
      return;
    }
    if (!/^\d{10}$/.test(mobile)) {
      toast({ title: 'મોબાઈલ નંબર ખોટો', description: '10 અંકનો નંબર દર્જ કરો' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name, mobile, car_model: carModel,
        package_id: pkgId,
        booking_date: date.toISOString(),
        payment_type: payment,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);

      // Track Lead event (form submitted successfully)
      fbTrack.lead({
        id: data.id,
        name: data.name,
        mobile: data.mobile,
        package_id: data.package_id,
        package_name: data.package_name,
        amount: data.amount,
      });

      const sessionId = data.cashfree_payment_session_id;
      const mode = data.cashfree_mode || 'sandbox';

      if (!sessionId) {
        toast({ title: 'Payment session ના બની', description: 'કૃપા કરી ફરી પ્રયાસ કરો અથવા +91 84606 92482 પર કૉલ કરો' });
        setLoading(false);
        return;
      }

      if (!window.Cashfree) {
        toast({ title: 'Payment gateway loading...', description: 'થોડી વારમાં ફરી પ્રયાસ કરો' });
        setLoading(false);
        return;
      }

      toast({ title: 'બુકિંગ બની ગયું!', description: `ID: ${data.id} — Cashfree પર જઈ રહ્યા છીએ...` });

      // Persist last booking id for return page fallback
      try { sessionStorage.setItem('osc_last_booking', data.id); } catch {}

      const cashfree = window.Cashfree({ mode });
      cashfree.checkout({
        paymentSessionId: sessionId,
        redirectTarget: '_self',
      });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'બુકિંગ નિષ્ફળ';
      toast({ title: 'Error', description: msg });
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange(v); }}>
      <DialogContent className="bg-[#0f0f16] border-purple-900/60 text-gray-200 max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="gradient-text text-2xl">બુકિંગ ફોર્મ</DialogTitle>
          <DialogDescription className="text-gray-400">તમારી વિગત ભરો — Cashfree પર secure પેમેન્ટ કરો. કન્ફર્મેશન WhatsApp પર મળશે.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">નામ *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Full Name" className="bg-[#14141c] border-purple-900/60 text-white mt-1.5" />
            </div>
            <div>
              <Label className="text-gray-300">મોબાઈલ નંબર *</Label>
              <Input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="10-digit mobile" className="bg-[#14141c] border-purple-900/60 text-white mt-1.5" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">કાર મોડલ *</Label>
              <Input value={carModel} onChange={e => setCarModel(e.target.value)} placeholder="e.g. Honda City 2020" className="bg-[#14141c] border-purple-900/60 text-white mt-1.5" />
            </div>
            <div>
              <Label className="text-gray-300">પેકેજ *</Label>
              <Select value={pkgId} onValueChange={setPkgId}>
                <SelectTrigger className="bg-[#14141c] border-purple-900/60 text-white mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141c] border-purple-900/60 text-white">
                  {ALL_PACKAGES.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — ₹{p.offerPrice.toLocaleString()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-gray-300">બુકિંગ તારીખ * <span className="text-xs text-gray-500">(રવિવાર બંધ)</span></Label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="mt-1.5 w-full flex items-center gap-2 h-10 px-3 rounded-md bg-[#14141c] border border-purple-900/60 text-left text-sm text-gray-200">
                  <CalIcon className="w-4 h-4 text-pink-400" />
                  {date ? date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'તારીખ પસંદ કરો'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="bg-[#0f0f16] border-purple-900/60 p-0 w-auto">
                <Calendar mode="single" selected={date} onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || d.getDay() === 0}
                  className="text-white" />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="text-gray-300 mb-2 block">પેમેન્ટ વિકલ્પ</Label>
            <RadioGroup value={payment} onValueChange={setPayment} className={`grid ${paymentOptions.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3`}>
              {paymentOptions.map(opt => (
                <label key={opt.id} className={`cursor-pointer rounded-xl border p-3 flex flex-col gap-1 ${payment === opt.id ? 'border-pink-500/70 bg-purple-950/20' : 'border-purple-900/60 bg-[#14141c]'}`}>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value={opt.id} id={`pay-${opt.id}`} className="border-pink-400 text-pink-400" />
                    <span className="text-white font-semibold text-sm">{opt.label}</span>
                  </div>
                  <div className="flex items-baseline gap-1 pl-6">
                    <IndianRupee className="w-3.5 h-3.5 text-pink-400" />
                    <span className="text-white font-bold">{opt.amount.toLocaleString()}</span>
                  </div>
                  <div className="text-[11px] text-gray-400 pl-6">{opt.hint}</div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="flex items-start gap-2 text-xs text-yellow-300/90 bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>નોંધ: ડીઝલ કાર ના ભાવમાં ફેરફાર હોઈ શકે (₹1,500+ પ્લસ). {payment === 'full' && <span className="block mt-1 font-semibold text-yellow-200">ફુલ પેમેન્ટ પર 18% GST લાગુ છે — ચુકવવાની રકમ MRP + 18% GST છે.</span>}</div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <div className="text-sm text-gray-400">ચુકવવાનું <span className="text-white font-bold">₹{selected.amount.toLocaleString()}</span></div>
            <Button type="submit" disabled={loading} className="gradient-btn rounded-full h-11 px-8 font-semibold w-full sm:w-auto">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> પ્રોસેસ...</>) : ('પે કરો & બુક કરો')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
